import React, { useEffect, useState, useMemo } from "react";
import {
  PageHeader,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  SpinnerLoader,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  Toastify,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import { RefreshCw } from "lucide-react";
import { IconMailCancel } from "@tabler/icons-react";
import apiClient from "@lift-admin/utils/apiClient";
import moment from "moment";
import { actionLog } from "../../App";

const PitchingToolOperationRunningTable = () => {
  const [allLogs, setAllLogs] = useState<any[]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState({
    logs: false,
    killOperation: false,
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    operationId: null as string | null,
    operationName: "",
    emailCount: 0,
  });

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const handleGetAllLogs = async () => {
    setLoading((prev) => ({ ...prev, logs: true }));
    try {
      const res = await apiClient.get("/crm/v1/bulk_emails/operations/pending");
      const data = res?.data?.response ?? [];
      const sortedLogs = [...data].sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      setAllLogs(sortedLogs);
    } catch {
      setAllLogs([]);
    } finally {
      setLoading((prev) => ({ ...prev, logs: false }));
    }
  };

  useEffect(() => {
    handleGetAllLogs();
  }, []);

  const handleKillOperation = (operation: any) => {
    setConfirmDialog({
      open: true,
      operationId: operation.operationId,
      operationName: operation.name,
      emailCount: operation.numberOfBulkEmails,
    });
  };

  const handleConfirmKill = async () => {
    try {
      setLoading((prev) => ({ ...prev, killOperation: true }));
      const operationName = confirmDialog.operationName;
      const res = await apiClient.post(`/crm/v1/bulk_emails/operation/kill`, {
        operationID: confirmDialog.operationId,
        userID: user?.id,
      });

      setConfirmDialog({
        open: false,
        operationId: null,
        operationName: "",
        emailCount: 0,
      });

      Toastify("success", `Successfully killed operation: ${operationName}`);
      await handleGetAllLogs();

      await actionLog?.actionLogTrack(
        JSON.stringify({
          message: `Pitching Tool Operation Killed: ${operationName}`,
          status: res.status,
          method: "POST",
          url: "/bulk_emails/operation/kill",
        }),
        false
      );
    } catch (error: any) {
      Toastify(
        "error",
        error?.response?.data?.message ||
          "Failed to kill operation. Please try again."
      );
      await actionLog?.actionLogTrack(
        JSON.stringify({
          message: `Error killing operation: ${confirmDialog.operationName} ${error?.response?.data?.message}`,
          status: error?.response?.data?.status,
          method: "POST",
          url: "/bulk_emails/operation/kill",
        }),
        true
      );
    } finally {
      setLoading((prev) => ({ ...prev, killOperation: false }));
    }
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      operationId: null,
      operationName: "",
      emailCount: 0,
    });
  };

  const paginatedLogs = useMemo(() => {
    const start = pageIndex * pageSize;
    return allLogs.slice(start, start + pageSize);
  }, [allLogs, pageIndex, pageSize]);

  const dataTableColumns = useMemo((): TableColumn<any>[] => [
    {
      key: "name",
      header: "Pending Operation Name",
      accessor: (row: any) => row?.name ?? "—",
      render: (_v: any, row: any) => (
        <div className="font-medium">{row?.name ?? "—"}</div>
      ),
    },
    {
      key: "numberOfBulkEmails",
      header: "Number of Emails",
      accessor: (row: any) => row?.numberOfBulkEmails ?? "—",
      render: (_v: any, row: any) => (
        <div>{row?.numberOfBulkEmails ?? "—"}</div>
      ),
    },
    {
      key: "timestamp",
      header: "Started At",
      accessor: (row: any) => row?.timestamp,
      render: (_v: any, row: any) => (
        <div>
          {row?.timestamp
            ? moment(new Date(row.timestamp)).format("MM-DD-YYYY hh:mm A")
            : "—"}
        </div>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      accessor: (row: any) => row?.operationId,
      width: "100px",
      align: "center" as const,
      render: (_v: any, row: any) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleKillOperation(row)}
              className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <IconMailCancel size={20} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Kill Operation</p>
          </TooltipContent>
        </Tooltip>
      ),
    },
  ], []);

  return (
    <TooltipProvider>
      <div className="flex flex-col h-full overflow-hidden">
        <PageHeader
          title="Pitching Tool Operations"
          renderCustomTop={
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleGetAllLogs}
                disabled={loading.logs}
                className="flex items-center gap-2 h-8 px-3"
              >
                {loading.logs ? (
                  <>
                    <SpinnerLoader className="h-4 w-4" />
                    Loading Logs
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    Refresh Logs
                  </>
                )}
              </Button>
            </div>
          }
        />

        <div className="flex-1 flex flex-col min-h-0 px-8 pt-8 pb-4">
          <div className="flex-1 min-h-0">
            <DataTable
              className="flex-1 min-h-0 overflow-auto"
              maxHeight="calc(100vh - 170px)"
              columns={dataTableColumns}
              data={paginatedLogs}
              loading={loading.logs}
              emptyMessage="No Operation Pending"
              stickyHeader
              stickyFooter
              showActions={false}
              pagination={{
                currentPage: pageIndex + 1,
                totalPages: Math.max(1, Math.ceil(allLogs.length / pageSize)),
                pageSize,
                totalItems: allLogs.length,
                onPageChange: (p) => setPageIndex(p - 1),
                onPageSizeChange: (size) => {
                  setPageSize(size);
                  setPageIndex(0);
                },
                pageSizeOptions: [25, 50, 100, 200, 500],
              }}
            />
          </div>
        </div>

        <Dialog
          open={confirmDialog.open}
          onOpenChange={(open) => {
            if (!open) handleCloseConfirmDialog();
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Kill Operation</DialogTitle>
              <DialogDescription>
                Are you sure you want to kill the operation{" "}
                <strong>{confirmDialog.operationName}</strong>? This will stop
                the bulk email operation that is currently processing{" "}
                <strong>{confirmDialog.emailCount}</strong> emails. This action
                cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={handleCloseConfirmDialog}
                disabled={loading.killOperation}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmKill}
                disabled={loading.killOperation}
              >
                {loading.killOperation ? (
                  <>
                    <SpinnerLoader className="h-4 w-4 mr-2" />
                    Killing Operation...
                  </>
                ) : (
                  "Yes, Kill Operation"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default PitchingToolOperationRunningTable;
